import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, BarGraph, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import {
  AnalyticsDashboardsPageDocument,
  AnalyticsDashboardsPageQuery,
  OneAnalyticsDashboardCategoryEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import DeleteDashboardDialog from 'modules/ai_reports/components/DeleteDashboardDialog'
import EditDashboardDialog from 'modules/ai_reports/components/EditDashboardDialog'
import type { DashboardNode } from 'modules/ai_reports/lib/types'
import { useOpenDashboardAction } from 'modules/ai/copilotActions/canvas/dashboards/useOpenDashboardAction'
import { F0Box } from '@factorialco/f0-react'

const DEFAULT_SORTING = { field: 'updatedAt', order: 'desc' } as const

// Only `analytics` is exposed in the listing today; the other categories
// (`communities`, `recruitment`, `trainings`) exist in translations + the
// agent but the corresponding listings haven't shipped yet.
const CATEGORY_VALUES: OneAnalyticsDashboardCategoryEnum[] = ['analytics']

const DashboardsListSection = () => {
  const { company } = useCurrentSession()
  const { openDashboardById, openChatForNewDashboard } = useOpenDashboardAction()
  const [itemToDelete, setItemToDelete] = useState<DashboardNode | null>(null)
  const [itemToEdit, setItemToEdit] = useState<DashboardNode | null>(null)

  const source = useGraphqlDataCollectionSource({
    totalItemSummary: (count: number) =>
      count !== undefined ? i18n.t('ai_reports.dashboards.count', { count }) : '',
    document: AnalyticsDashboardsPageDocument,
    mapVariables: ({ sortings, filters }) => ({
      sortOrder: sortings?.[0] ?? DEFAULT_SORTING,
      companyId: company.id,
      category: filters.category?.[0],
    }),
    mapData: (data: AnalyticsDashboardsPageQuery) => data.oneAnalytics.dashboardsConnection.nodes,
    mapPagination: (data: AnalyticsDashboardsPageQuery) => ({
      total: data.oneAnalytics.dashboardsConnection.totalCount ?? 0,
    }),
    itemOnClick: (item: DashboardNode) => () => {
      void openDashboardById(item.id)
    },
    defaultSortings: DEFAULT_SORTING,
    sortings: {
      title: { label: i18n.t('ai_reports.dashboards.title_column') },
      authorId: { label: i18n.t('ai_reports.dashboards.created_by') },
      createdAt: { label: i18n.t('ai_reports.dashboards.created_at') },
      updatedAt: { label: i18n.t('ai_reports.dashboards.last_update') },
    },
    filters: {
      category: {
        label: i18n.t('ai_reports.dashboards.category'),
        type: 'in' as const,
        options: {
          options: CATEGORY_VALUES.map((value) => ({
            value,
            label: value,
          })),
        },
      },
    },
    paginationType: 'pages',
    perPage: 15,
    itemActions: (item: DashboardNode) => [
      {
        label: i18n.t('common.edit'),
        icon: Pencil,
        onClick: () => setItemToEdit(item),
      },
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
        onClick: () => setItemToDelete(item),
      },
    ],
    primaryActions: () => ({
      label: i18n.t('ai_reports.dashboards.create_report'),
      icon: Add,
      onClick: () => openChatForNewDashboard(),
    }),
  })

  return (
    <F0Box padding="md">
      <DeleteDashboardDialog itemToDelete={itemToDelete} setItemToDelete={setItemToDelete} />
      <EditDashboardDialog
        key={itemToEdit?.id}
        itemToEdit={itemToEdit}
        setItemToEdit={setItemToEdit}
      />
      <OneDataCollection
        source={source}
        fullHeight
        emptyStates={{
          'no-data': {
            emoji: '📊',
            title: i18n.t('ai_reports.dashboards.empty_state.no_data.title'),
            description: i18n.t('ai_reports.dashboards.empty_state.no_data.description'),
          },
        }}
        visualizations={[
          {
            type: 'list',
            options: {
              itemDefinition: (item: DashboardNode) => ({
                title: item.title,
                description: item.description?.trim() ? [item.description] : undefined,
                avatar: { type: 'icon', icon: BarGraph },
              }),
              fields: [
                {
                  label: i18n.t('ai_reports.dashboards.category'),
                  render: (item: DashboardNode) => ({
                    type: 'tag',
                    value: {
                      type: 'raw',
                      label: item.category,
                    },
                  }),
                },
                {
                  label: i18n.t('ai_reports.dashboards.last_update'),
                  render: (item: DashboardNode) => ({
                    type: 'date',
                    value: new Date(item.updatedAt),
                  }),
                },
                {
                  label: i18n.t('ai_reports.dashboards.created_by'),
                  render: (item: DashboardNode) => {
                    if (!item.author) return '-'
                    return {
                      type: 'person',
                      value: { ...item.author, src: buildUrl(item.author.avatar?.url) },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
    </F0Box>
  )
}

export default DashboardsListSection
